import { useEffect, useState } from "react";

import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase";

import {
  FaChevronDown,
  FaChevronUp,
  FaArrowUp,
} from "react-icons/fa";

import "./WithdrawRecords.css";


/* =========================================================
   COUNTRY INFORMATION
========================================================= */

const countryInfo = {

  TZS: {
    name: "Tanzania",
    flag: "🇹🇿",
  },

  KES: {
    name: "Kenya",
    flag: "🇰🇪",
  },

  UGX: {
    name: "Uganda",
    flag: "🇺🇬",
  },

  RWF: {
    name: "Rwanda",
    flag: "🇷🇼",
  },

  BIF: {
    name: "Burundi",
    flag: "🇧🇮",
  },

  ZMW: {
    name: "Zambia",
    flag: "🇿🇲",
  },

  MWK: {
    name: "Malawi",
    flag: "🇲🇼",
  },

  MZN: {
    name: "Mozambique",
    flag: "🇲🇿",
  },

  USD: {
    name: "Zimbabwe",
    flag: "🇿🇼",
  },

  SSP: {
    name: "South Sudan",
    flag: "🇸🇸",
  },

  BWP: {
    name: "Botswana",
    flag: "🇧🇼",
  },

  MGA: {
    name: "Madagascar",
    flag: "🇲🇬",
  },

};


/* =========================================================
   FORMAT NUMBER
========================================================= */

const formatNumber = (value) => {

  const number =
    Number(value || 0);

  return number.toLocaleString(
    undefined,
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

};


/* =========================================================
   GET DATE VALUE
========================================================= */

const getDateValue = (value) => {

  if (!value) {
    return 0;
  }


  try {

    if (
      typeof value === "object" &&
      typeof value.toMillis === "function"
    ) {

      return value.toMillis();

    }


    if (
      typeof value === "object" &&
      typeof value.toDate === "function"
    ) {

      return value
        .toDate()
        .getTime();

    }


    if (
      typeof value === "number"
    ) {

      return value;

    }


    const parsed =
      new Date(value).getTime();


    return Number.isNaN(parsed)
      ? 0
      : parsed;

  } catch {

    return 0;

  }

};


/* =========================================================
   FORMAT DATE
========================================================= */

const formatDate = (value) => {

  const timestamp =
    getDateValue(value);


  if (!timestamp) {
    return "Date unavailable";
  }


  try {

    const date =
      new Date(timestamp);


    const year =
      date.getFullYear();


    const month =
      String(
        date.getMonth() + 1
      ).padStart(2, "0");


    const day =
      String(
        date.getDate()
      ).padStart(2, "0");


    return `${year}-${month}-${day}`;

  } catch {

    return "Date unavailable";

  }

};


/* =========================================================
   FORMAT TIME
========================================================= */

const formatTime = (value) => {

  const timestamp =
    getDateValue(value);


  if (!timestamp) {
    return "--:--:--";
  }


  try {

    return new Date(
      timestamp
    ).toLocaleTimeString(
      [],
      {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }
    );

  } catch {

    return "--:--:--";

  }

};


/* =========================================================
   FORMAT FULL DATE
========================================================= */

const formatFullDate = (value) => {

  const timestamp =
    getDateValue(value);


  if (!timestamp) {
    return "Date unavailable";
  }


  try {

    return new Date(
      timestamp
    ).toLocaleString(
      undefined,
      {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }
    );

  } catch {

    return "Date unavailable";

  }

};


/* =========================================================
   MASK PHONE
========================================================= */

const maskNumber = (
  number = ""
) => {

  const value =
    String(number || "")
      .replace(/\s/g, "")
      .replace(/-/g, "")
      .trim();


  if (!value) {
    return "Not available";
  }


  if (
    value.length <= 4
  ) {

    return value;

  }


  return `****${value.slice(-4)}`;

};


/* =========================================================
   STATUS CLASS
========================================================= */

const getStatusClass = (
  status
) => {

  const value =
    String(
      status || ""
    )
      .toLowerCase()
      .trim();


  switch (value) {

    case "processing":
      return "withdraw-status-processing";


    case "reviewing":
      return "withdraw-status-reviewing";


    case "reviewed":
      return "withdraw-status-reviewed";


    case "success":
    case "successful":
    case "completed":
    case "complete":
    case "approved":
    case "paid":

      return "withdraw-status-success";


    case "cancelled":
    case "canceled":
    case "error":
    case "failed":
    case "rejected":
    case "declined":

      return "withdraw-status-failed";


    case "pending":
    case "waiting":

      return "withdraw-status-pending";


    default:
      return "withdraw-status-other";

  }

};


/* =========================================================
   STATUS LABEL
========================================================= */

const getStatusLabel = (
  status
) => {

  const value =
    String(
      status || ""
    )
      .toLowerCase()
      .trim();


  switch (value) {

    case "processing":
      return "Processing";


    case "reviewing":
      return "Reviewing";


    case "reviewed":
      return "Reviewed";


    case "success":
    case "successful":
    case "completed":
    case "complete":
    case "approved":
    case "paid":

      return "Successful";


    case "cancelled":
    case "canceled":

      return "Cancelled";


    case "error":
    case "failed":
    case "rejected":
    case "declined":

      return "Failed";


    case "pending":
    case "waiting":

      return "Pending";


    default:
      return status || "Pending";

  }

};


/* =========================================================
   COMPONENT
========================================================= */

export default function WithdrawRecord() {


  /* =======================================================
     USERNAME
  ======================================================= */

  const username =
    localStorage.getItem(
      "username"
    ) || "";


  /* =======================================================
     RECORDS
  ======================================================= */

  const [
    records,
    setRecords,
  ] = useState([]);


  /* =======================================================
     LOADING
  ======================================================= */

  const [
    loading,
    setLoading,
  ] = useState(true);


  /* =======================================================
     REFRESHING
  ======================================================= */

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);


  /* =======================================================
     ERROR
  ======================================================= */

  const [
    error,
    setError,
  ] = useState("");


  /* =======================================================
     EXPANDED RECORD
  ======================================================= */

  const [
    expandedRecord,
    setExpandedRecord,
  ] = useState(null);


  /* =======================================================
     LOAD RECORDS
  ======================================================= */

  const loadRecords =
    async (
      isRefresh = false
    ) => {

      if (!username) {

        setLoading(false);

        setError(
          "User session was not found."
        );

        return;

      }


      try {

        if (isRefresh) {

          setRefreshing(true);

        } else {

          setLoading(true);

        }


        setError("");


        /* ===============================================
           COLLECTION
        =============================================== */

        const withdrawalsRef =
          collection(
            db,
            "withdrawals"
          );


        /* ===============================================
           CURRENT USER QUERY
        =============================================== */

        const withdrawalsQuery =
          query(
            withdrawalsRef,

            where(
              "username",
              "==",
              username
            )
          );


        /* ===============================================
           GET DATA
        =============================================== */

        const snapshot =
          await getDocs(
            withdrawalsQuery
          );


        /* ===============================================
           MAP RECORDS
        =============================================== */

        const loadedRecords =
          snapshot.docs
            .map(
              (withdrawalDoc) => ({

                id:
                  withdrawalDoc.id,

                ...withdrawalDoc.data(),

              })
            )
            .sort(
              (a, b) =>
                getDateValue(
                  b.createdAt
                ) -
                getDateValue(
                  a.createdAt
                )
            );


        setRecords(
          loadedRecords
        );


      } catch (err) {

        console.error(
          "Withdraw record loading error:",
          err
        );


        setError(
          err?.message ||
          "Unable to load withdrawal records."
        );

      } finally {

        setLoading(false);

        setRefreshing(false);

      }

    };


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {

    loadRecords();

  }, [username]);


  /* =======================================================
     SUMMARY
  ======================================================= */

  const totalRecords =
    records.length;


  const processingRecords =
    records.filter(
      (item) => {

        const status =
          String(
            item.status || ""
          )
            .toLowerCase()
            .trim();


        return (
          status === "processing" ||
          status === "reviewing" ||
          status === "reviewed" ||
          status === "pending"
        );

      }
    ).length;


  const successfulRecords =
    records.filter(
      (item) => {

        const status =
          String(
            item.status || ""
          )
            .toLowerCase()
            .trim();


        return (
          status === "success" ||
          status === "successful" ||
          status === "completed" ||
          status === "complete" ||
          status === "approved" ||
          status === "paid"
        );

      }
    ).length;


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {

    return (

      <div className="withdraw-record-page">

        <div className="withdraw-record-loading">

          <div className="withdraw-record-spinner" />

          <strong>
            Loading withdrawal records...
          </strong>

          <span>
            Please wait
          </span>

        </div>

      </div>

    );

  }


  /* =======================================================
     MAIN
  ======================================================= */

  return (

    <div className="withdraw-record-page">


      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="withdraw-record-header">

        <div className="withdraw-record-header-text">

          <h1>
            Withdrawal Record
          </h1>

          <p>
            Your withdrawal history
          </p>

        </div>


        <button
          type="button"
          className="withdraw-record-refresh"
          onClick={() =>
            loadRecords(true)
          }
          disabled={
            refreshing
          }
        >

          {refreshing
            ? "Refreshing..."
            : "Refresh"}

        </button>

      </div>


      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (

        <div className="withdraw-record-alert">

          <span className="withdraw-record-alert-icon">
            !
          </span>

          <span>
            {error}
          </span>

        </div>

      )}


      {/* =====================================================
          SUMMARY
      ===================================================== */}

      <div className="withdraw-record-summary">


        <div className="withdraw-record-summary-card">

          <span>
            Total Withdrawals
          </span>

          <strong>
            {totalRecords}
          </strong>

        </div>


        <div className="withdraw-record-summary-card">

          <span>
            Processing
          </span>

          <strong>
            {processingRecords}
          </strong>

        </div>


        <div className="withdraw-record-summary-card">

          <span>
            Successful
          </span>

          <strong>
            {successfulRecords}
          </strong>

        </div>


      </div>


      {/* =====================================================
          EMPTY
      ===================================================== */}

      {records.length === 0 ? (

        <div className="withdraw-record-empty">

          <div className="withdraw-record-empty-icon">

            <FaArrowUp />

          </div>


          <strong>
            No withdrawal records
          </strong>


          <span>
            Your withdrawal history will appear here.
          </span>

        </div>

      ) : (

        <div className="withdraw-record-list">


          {records.map(
            (item) => {


              /* =============================================
                 CURRENCY
              ============================================= */

              const currency =
                String(
                  item.currency ||
                  "TZS"
                ).toUpperCase();


              /* =============================================
                 AMOUNT
              ============================================= */

              const amount =
                Number(
                  item.amount ||
                  0
                );


              /* =============================================
                 ACTUAL AMOUNT
              ============================================= */

              const actualAmount =
                Number(
                  item.actualAmount ||
                  0
                );


              /* =============================================
                 FEE AMOUNT
              ============================================= */

              const feeAmount =
                Number(
                  item.feeAmount ||
                  0
                );


              /* =============================================
                 STATUS
              ============================================= */

              const status =
                String(
                  item.status ||
                  "processing"
                )
                  .toLowerCase()
                  .trim();


              /* =============================================
                 COUNTRY
              ============================================= */

              const country =
                countryInfo[
                  currency
                ] || {

                  name:
                    item.country ||
                    "Unknown",

                  flag:
                    "🌍",

                };


              /* =============================================
                 WALLET
              ============================================= */

              const wallet =
                item.wallet ||
                {};


              const walletNetwork =
                item.walletNetwork ||
                wallet.network ||
                "Mobile Wallet";


              const walletNumber =
                item.walletNumber ||
                wallet.number ||
                wallet.phoneNumber1 ||
                "";


              const walletCode =
                item.walletCode ||
                wallet.code ||
                "";


              /* =============================================
                 EXPANDED
              ============================================= */

              const isExpanded =
                expandedRecord ===
                item.id;


              /* =============================================
                 RECORD
              ============================================= */

              return (

                <div
                  key={
                    item.id
                  }

                  className={`
                    withdraw-record-item
                    ${
                      isExpanded
                        ? "is-open"
                        : ""
                    }
                  `}
                >


                  {/* =========================================
                      MAIN HORIZONTAL ROW
                  ========================================= */}

                  <div className="withdraw-record-main-row">


                    {/* =======================================
                        LEFT
                    ======================================= */}

                    <div className="withdraw-record-main-left">


                      <div className="withdraw-record-type">

                        Withdrawal

                      </div>


                      <div className="withdraw-record-date">

                        <span>
                          {formatDate(
                            item.createdAt
                          )}
                        </span>


                        <span className="withdraw-record-time">

                          {formatTime(
                            item.createdAt
                          )}

                        </span>

                      </div>

                    </div>


                    {/* =======================================
                        RIGHT
                    ======================================= */}

                    <div className="withdraw-record-main-right">


                      <div className="withdraw-record-main-info">


                        <div className="withdraw-record-main-amount">

                          {formatNumber(
                            amount
                          )}{" "}

                          {currency}

                        </div>


                        <div
                          className={`
                            withdraw-record-main-status
                            ${getStatusClass(
                              status
                            )}
                          `}
                        >

                          {getStatusLabel(
                            status
                          )}

                        </div>


                      </div>


                      {/* =====================================
                          EXPAND BUTTON
                      ===================================== */}

                      <button
                        type="button"

                        className={`
                          withdraw-record-arrow
                          ${
                            isExpanded
                              ? "is-open"
                              : ""
                          }
                        `}

                        onClick={() => {

                          setExpandedRecord(
                            isExpanded
                              ? null
                              : item.id
                          );

                        }}

                        aria-label={
                          isExpanded
                            ? "Hide withdrawal details"
                            : "Show withdrawal details"
                        }
                      >

                        {isExpanded ? (

                          <FaChevronUp />

                        ) : (

                          <FaChevronDown />

                        )}

                      </button>

                    </div>

                  </div>


                  {/* =================================================
                      DETAILS
                  ================================================= */}

                  {isExpanded && (

                    <div className="withdraw-record-details-panel">


                      {/* =========================================
                          TYPE
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Type
                        </span>

                        <strong>
                          {item.withdrawalType ||
                            "Withdrawal"}
                        </strong>

                      </div>


                      {/* =========================================
                          STATUS
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Status
                        </span>

                        <strong
                          className={getStatusClass(
                            status
                          )}
                        >

                          {getStatusLabel(
                            status
                          )}

                        </strong>

                      </div>


                      {/* =========================================
                          DATE
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Date
                        </span>

                        <strong>
                          {formatDate(
                            item.createdAt
                          )}
                        </strong>

                      </div>


                      {/* =========================================
                          TIME
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Time
                        </span>

                        <strong>
                          {formatTime(
                            item.createdAt
                          )}
                        </strong>

                      </div>


                      {/* =========================================
                          WITHDRAWAL AMOUNT
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Withdrawal Amount
                        </span>

                        <strong>

                          {formatNumber(
                            amount
                          )}{" "}

                          {currency}

                        </strong>

                      </div>


                      {/* =========================================
                          RECEIVE AMOUNT
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Amount to Receive
                        </span>

                        <strong className="withdraw-detail-receive">

                          {formatNumber(
                            actualAmount
                          )}{" "}

                          {currency}

                        </strong>

                      </div>


                      {/* =========================================
                          FEE
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Withdrawal Fee
                        </span>

                        <strong>

                          {item.fee !==
                          undefined
                            ? `${item.fee}%`
                            : "-"}

                          <small>

                            {" "}
                            (
                            {currency}{" "}
                            {formatNumber(
                              feeAmount
                            )}
                            )

                          </small>

                        </strong>

                      </div>


                      {/* =========================================
                          WALLET NETWORK
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Payment Wallet
                        </span>

                        <strong>
                          {walletNetwork}
                        </strong>

                      </div>


                      {/* =========================================
                          WALLET NUMBER
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Wallet Number
                        </span>

                        <strong className="withdraw-wallet-number">

                          {walletCode}

                          {maskNumber(
                            walletNumber
                          )}

                        </strong>

                      </div>


                      {/* =========================================
                          COUNTRY
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Country
                        </span>

                        <strong>

                          {country.flag}{" "}

                          {country.name}

                        </strong>

                      </div>


                      {/* =========================================
                          PROCESSING TIME
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Processing Time
                        </span>

                        <strong>

                          {item.processingTime ||
                            "-"}

                        </strong>

                      </div>


                      {/* =========================================
                          REFERENCE
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Reference
                        </span>

                        <strong
                          className="withdraw-reference"
                          title={
                            item.id
                          }
                        >

                          {item.id
                            ? `${item.id.slice(
                                0,
                                14
                              )}...`
                            : "-"}

                        </strong>

                      </div>


                      {/* =========================================
                          FULL DATE
                      ========================================= */}

                      <div className="withdraw-detail-row">

                        <span>
                          Created
                        </span>

                        <strong>

                          {formatFullDate(
                            item.createdAt
                          )}

                        </strong>

                      </div>


                    </div>

                  )}

                </div>

              );

            }
          )}

        </div>

      )}

    </div>

  );

}
